import { fromResizeObserver } from '@schmancy/directives/layout'
import { reducedMotion$ } from '@schmancy/directives/reduced-motion'
import { SchmancyElement, schmancyElement } from '@schmancy/mixins/index'
import { css, html, type PropertyValues } from 'lit'
import { property, query, state } from 'lit/decorators.js'
import { when } from 'lit/directives/when.js'
import {
	animationFrames,
	animationFrameScheduler,
	auditTime,
	catchError,
	EMPTY,
	exhaustMap,
	finalize,
	from,
	fromEvent,
	map,
	merge,
	startWith,
	Subject,
	switchMap,
	takeUntil,
	tap,
	timer,
} from 'rxjs'
import { SchmancyScroll } from './scroll'
import '@schmancy/primitives/icons/icon'
import '@schmancy/primitives/surface'

/** How far out the `nudge` capability carries the real scroll offset, and over
 *  how long it goes and comes back. */
const NUDGE_PX = 34
const NUDGE_MS = 900

/**
 * Wraps a `<schmancy-scroll>` and paints the LIVING EDGE a region that hides
 * its scrollbar owes its operator: the cue for "there is more this way" lives
 * AT the scroll edge and nowhere else.
 *
 * ```html
 * <schmancy-scroll-rail>
 *   <schmancy-scroll direction="horizontal" hide>…</schmancy-scroll>
 * </schmancy-scroll-rail>
 * ```
 *
 * A WRAPPER RATHER THAN A FLAG ON THE SCROLLER, because `schmancy-scroll`'s
 * host IS its scrollport (`get scroller() { return this }`): anything the
 * scroller rendered alongside its slot would scroll away with the content, and
 * restructuring the host into a grid would move `part="scroller"` off the
 * element every consumer reads `.scrollLeft` from. So the rail owns the
 * positioning context and the scroller stays untouched. Its whole API is one
 * boolean, off — a consumer opts in by wrapping, and one that wraps and says
 * nothing else gets the shade, the mark, and a band that never moves on its
 * own.
 *
 * THE EDGE GOES INTO SHADE, AND THE SHADE TURNS WITH THE SCHEME. A surface
 * that continues past a boundary recedes from the light, and what recedes is
 * the GROUND — the ink printed on it is the last thing a fold takes.
 * The shade deepens toward `surface-inverse`, whose neutral fill turns with
 * the scheme: dark over a light band, light over a dark band. The ramp carries
 * its weight in the outer third and trails to nothing. Its composited contrast
 * depends on the actual content beneath it. There is no hairline on the cut;
 * the deepest stop of the shade is its own edge.
 *
 * THE MARK CARRIES THE DIRECTION, AND CARRIES ITS OWN GROUND. The shade says
 * the band continues; the mark says which way. Bare ink says it against
 * whatever sits under the cut — which is the consumer's content, not this
 * component's, and scrolls. An ink-only chevron would therefore inherit an
 * unknown contrast pair from the consumer's content.
 *
 * So the mark is a body: 44px of `containerHighest` at the `solid` rung, ringed
 * in `outline`, hung a third of its own width out past the edge so it rests ON
 * the boundary rather than inside the band, carrying the chevron at its natural
 * size. That is a pair the component owns end to end — the glyph is the
 * on-colour of the body it sits on. Where the content matches the
 * body's own tone the disc disappears into it, and the glyph, which contrasts
 * with the body, therefore contrasts with that content too. `containerHighest`
 * rather than the `surface-inverse` the shade deepens toward, because a tone
 * near the scheme's own is the one that SCALES — content agreeing with the
 * scheme takes a disc that recedes into it, content disagreeing takes one that
 * asserts, which is exactly the ground bare ink had nothing to say on. The
 * `solid` rung and not the material's default fill, because a half-fill
 * composites the unknown content back into the pair the body was added to
 * close. No tone is measured and none can be: what is behind the mark is a card
 * in flight, different every frame of a scroll and different again for the next
 * consumer.
 *
 * It still says it by GOING that way — the body reaches out along its own axis
 * and returns, one long eased beat, so it reads as breath rather than blinking.
 * Neither that nor the shade touches the scroll offset: the band moves when the
 * operator moves it, or once, on its own, if the consumer asked for `nudge`.
 * `prefers-reduced-motion: reduce` stills the reach, which costs the cue
 * nothing it was standing on — a chevron on its own body at the edge it points
 * at. Each cue is absent the moment there is nothing more that way, and the
 * rail draws nothing at all while the content fits, which is what makes the
 * pair a statement of position and not decoration.
 *
 * Out of the accessibility tree entirely: everything it does is a shortcut over
 * content already in DOM order, so a screen reader gains nothing from it and a
 * keyboard reaches every column without it. 44px of hit area, because a
 * tablet's finger is the same finger everywhere.
 *
 * @fires {CustomEvent<{scrollLeft: number, max: number, leading: boolean, trailing: boolean, nudging: boolean}>} edge - The band's live position, on every frame it moves.
 * @csspart shade-leading - The ramp over the leading cut.
 * @csspart shade-trailing - The ramp over the trailing cut.
 * @csspart mark-leading - The mark riding the leading fold.
 * @csspart mark-trailing - The mark riding the trailing fold.
 */
@schmancyElement('schmancy-scroll-rail')
export class SchmancyScrollRail extends SchmancyElement {
	/**
	 * Move the band once, on its own: on the first paint at which it overflows,
	 * the REAL scroll offset travels 34px outward and eases back — one breath,
	 * never repeated, so the operator sees the surface move before they are asked
	 * to move it. Keyed on that paint and not on connect, because a band that has
	 * not yet measured itself has nothing to demonstrate; spent once per mount,
	 * and re-armed only by the consumer turning this on again. The leading cue is
	 * held off for its duration, since a band nudged off its own start has not
	 * actually left anything behind. `prefers-reduced-motion: reduce` drops it
	 * whole. Off, the band moves when the operator moves it and at no other time.
	 */
	@property({ type: Boolean, reflect: true }) nudge = false

	/** There is content behind the leading edge / past the trailing edge. Both
	 *  are read off the live scroll offset on every frame the band moves. */
	@state() private leading = false
	@state() private trailing = false

	/** The nudge is in flight, which is the one interval where a non-zero scroll
	 *  offset is the band's own doing and not the operator's. */
	@state() private nudging = false

	/** The nudge has already been spent for this arming. */
	private spent = false
	private readonly nudges = new Subject<void>()
	/** A re-measure the band's own geometry sources cannot announce — a consumer
	 *  turning `nudge` on later changes no size and fires no scroll, so it says
	 *  so here. */
	private readonly remeasure = new Subject<void>()

	@query('slot', true) private slotEl!: HTMLSlotElement

	/** The reach is the component's own motion, so its keyframes are its own
	 *  too; everything that positions, colours or sizes the mark is a utility on
	 *  it. The reach rides the BODY and never the node carrying the mark's
	 *  position, because a keyframe naming `transform` replaces the whole of it
	 *  — an animation on the positioned node would drop the very translate that
	 *  hangs the mark out over the cut. */
	static styles = [
		css`
			:host {
				display: grid;
				position: relative;
				min-height: 0;
			}
			@keyframes reach-right {
				0%,
				100% {
					transform: translateX(0);
				}
				42% {
					transform: translateX(3px);
				}
			}
			@keyframes reach-left {
				0%,
				100% {
					transform: translateX(0);
				}
				42% {
					transform: translateX(-3px);
				}
			}
		`,
	]

	override connectedCallback(): void {
		super.connectedCallback()
		// One chain: the wrapped band's own geometry, read on every frame it
		// moves, merged with the nudge that geometry arms — one subscription owns
		// both, so the flight tears down with the reading that started it.
		// Re-keyed on slotchange because the scroller is the consumer's element,
		// not this one's, and arrives (or is swapped) whenever the consumer's own
		// render says so.
		merge(
			from(this.updateComplete).pipe(
				switchMap(() => fromEvent(this.slotEl, 'slotchange').pipe(startWith(null))),
				switchMap(() => {
					const el = this.scroller()
					if (el === null) {
						this.leading = false
						this.trailing = false
						return EMPTY
					}
					// Content — not just container — geometry states the edges, and
					// the two need separate observers: the scroller resizing is what
					// its own ResizeObserver sees, while content growing INSIDE a
					// fixed-width scroller resizes nothing the scroller can observe
					// about itself. So each of the scroller's own children is
					// observed too.
					return merge(
						fromEvent(el, 'scroll'),
						fromEvent(window, 'resize'),
						fromResizeObserver(el).pipe(catchError(() => EMPTY)),
						this.remeasure,
						...Array.from(el.children).map(child => fromResizeObserver(child).pipe(catchError(() => EMPTY))),
					).pipe(
						startWith(null),
						// auditTime, never debounceTime: a debounce emits only once the
						// events STOP, so an edge state hung on one sits frozen through the
						// whole of a smooth scroll and catches up only at rest — right at
						// every moment nobody is looking at it.
						auditTime(0, animationFrameScheduler),
						tap(() => this.read()),
					)
				}),
			),
			// exhaustMap, not switchMap: a re-arm arriving mid-flight is dropped
			// rather than restarting the flight, because a restart reads the
			// already-nudged offset as the resting one and walks the band outward one
			// arming at a time.
			this.nudges.pipe(
				exhaustMap(() => {
					const el = this.scroller()
					if (el === null || reducedMotion$.value) return EMPTY
					const rest = el.scrollLeft
					this.nudging = true
					return animationFrames().pipe(
						map(({ elapsed }) => Math.min(elapsed / NUDGE_MS, 1)),
						// The TIMER ends the flight, never the frame count:
						// requestAnimationFrame stops in a throttled or hidden tab, so a
						// flight ended by its own frames simply stops arriving there —
						// leaving the band parked off its rest offset with `nudging` stuck
						// true and the leading cue suppressed for the rest of the mount,
						// which reads as a band that moved itself and never came back. A
						// setTimeout-backed terminator fires whether or not a frame does.
						takeUntil(timer(NUDGE_MS)),
						tap(u => {
							// Out on a cubic ease-out over the first 40% — which spends its
							// last third within a pixel of the far point, so the reach reads
							// as arriving rather than as turning — and back on a cosine
							// ease-in-out over the remaining 60%.
							// scrollTo({ behavior: 'instant' }), never a scrollLeft write:
							// the scroller carries scroll-behavior: smooth, which routes
							// EVERY programmatic scroll through the browser's own smooth
							// animator, so a per-frame write re-targets an animation that
							// never arrives and the offset sits at rest for the whole
							// flight, silently.
							el.scrollTo({
								behavior: 'instant',
								left:
									rest +
									NUDGE_PX * (u < 0.4 ? 1 - (1 - u / 0.4) ** 3 : (1 + Math.cos(Math.PI * ((u - 0.4) / 0.6))) / 2),
							})
						}),
						// Runs on the timer's end and on teardown alike, so a band whose
						// rail disconnects mid-flight is put back too.
						finalize(() => {
							el.scrollTo({ behavior: 'instant', left: rest })
							this.nudging = false
						}),
					)
				}),
			),
		)
			.pipe(takeUntil(this.disconnecting))
			.subscribe()
	}

	/** Turning `nudge` on is that capability's own first paint, so it re-arms and
	 *  re-measures for it — without which a consumer that binds the flag to a
	 *  signal would get the motion only on a mount that happened to start with it
	 *  set. */
	protected override updated(changed: PropertyValues): void {
		super.updated(changed)
		if (!changed.has('nudge') || !this.nudge) return
		this.spent = false
		// Through the geometry leg, never straight into read(): that leg is
		// audited onto an animation frame, which puts the read — and the state it
		// writes — after the update cycle this callback is still inside.
		this.remeasure.next()
	}

	/** The wrapped scrollport. `instanceof` rather than a tag-name test, so the
	 *  rail holds a reference it can actually drive rather than one it hopes
	 *  has been upgraded — and the optional slot because `page()` is public and
	 *  a consumer can reach it before this element has ever rendered. */
	private scroller(): SchmancyScroll | null {
		for (const node of this.slotEl?.assignedElements() ?? []) if (node instanceof SchmancyScroll) return node
		return null
	}

	/** Reads the live scroll offset, states the two edges off it, arms the nudge
	 *  on the first paint that has something to demonstrate, and says so. The
	 *  `edge` event is the only way anything outside this shadow root can know
	 *  the cues repainted — a probe measuring the band's own geometry can see the
	 *  offset move but not when the rail has answered it. */
	private read(): void {
		const el = this.scroller()
		if (el === null) return
		const max = el.scrollWidth - el.clientWidth
		const overflows = max > 1
		// A band nudged off its own start has left nothing behind, so the leading
		// cue is held off for the flight's duration. `nudging` is only ever true
		// under `nudge`, which is why this reads identically to
		// `overflows && el.scrollLeft > 2` without it.
		this.leading = overflows && !this.nudging && el.scrollLeft > 2
		this.trailing = overflows && el.scrollLeft < max - 2
		if (this.nudge && overflows && !this.spent) {
			this.spent = true
			this.nudges.next()
		}
		this.dispatchEvent(
			new CustomEvent('edge', {
				detail: {
					scrollLeft: Math.round(el.scrollLeft),
					max: Math.round(max),
					leading: this.leading,
					trailing: this.trailing,
					// Whether this offset is the band's own doing. The flight is over in
					// under a second, which is shorter than any still frame a capture
					// tool can take, so a frame cannot show it happening — the offsets
					// reported while this is true are what stands in.
					nudging: this.nudging,
				},
				bubbles: true,
				composed: true,
			}),
		)
	}

	/** One column further along, in whichever direction — what a mark press
	 *  does. The stop is the next column's own leading edge, never a fixed pixel
	 *  step: a band paged by a constant lands mid-column the moment a gap or a
	 *  measure changes.
	 *
	 *  Public because the marks are deliberately out of the accessibility tree
	 *  (see the header), which puts them out of reach of every name-addressed
	 *  driver too — so a harness, a test, or a consumer's own named control
	 *  drives this same path rather than an improvised probe. */
	public page(direction: 1 | -1): void {
		const el = this.scroller()
		const track = el?.firstElementChild
		if (el === null || track === null || track === undefined) return
		// Read against the SCROLLER's own coordinate space — offsetLeft is
		// measured from whichever ancestor happens to be the offsetParent, and a
		// scroller that is not one hands back every column shifted by a constant.
		const origin = el.getBoundingClientRect().left - el.scrollLeft
		const edges = Array.from(track.children).map(c => c.getBoundingClientRect().left - origin)
		const next = direction === 1 ? edges.find(x => x > el.scrollLeft + 2) : edges.findLast(x => x < el.scrollLeft - 2)
		el.scrollToLeft(next ?? (direction === 1 ? el.scrollWidth : 0))
	}

	protected render() {
		return html`
			<slot></slot>

			<!-- The two shades. Painted toward the scheme-turning surface-inverse pair,
			     weighted in the outer third, and out of the pointer path entirely — a
			     cue that eats a drag is a cue that broke the thing it was explaining. -->
			${when(
				this.leading,
				() => html`
					<div
						part="shade-leading"
						aria-hidden="true"
						class="pointer-events-none absolute inset-y-0 left-0 w-16 bg-linear-to-r from-surface-inverse/50 via-surface-inverse/13 via-30% to-transparent"
					></div>
				`,
			)}
			${when(
				this.trailing,
				() => html`
					<div
						part="shade-trailing"
						aria-hidden="true"
						class="pointer-events-none absolute inset-y-0 right-0 w-16 bg-linear-to-l from-surface-inverse/50 via-surface-inverse/13 via-30% to-transparent"
					></div>
				`,
			)}

			<!-- The two marks, each a body straddling its cut. Two nodes rather than
			     one: the outer holds the mark's position over the fold and the hover
			     it answers a pointer with, the inner is the body and takes the reach,
			     so neither transform can replace the other. -->
			${when(
				this.leading,
				() => html`
					<div
						part="mark-leading"
						aria-hidden="true"
						class="absolute top-1/2 left-0 z-10 grid size-11 -translate-x-1/3 -translate-y-1/2 cursor-pointer place-items-center transition-transform duration-200 ease-out hover:scale-110"
						@click=${() => this.page(-1)}
					>
						<schmancy-surface
							type="containerHighest"
							opacity="solid"
							class="grid size-full animate-[reach-left_2.8s_cubic-bezier(0.4,0,0.2,1)_infinite] place-items-center rounded-full ring-1 ring-control-border motion-reduce:animate-none"
						>
							<schmancy-icon>chevron_left</schmancy-icon>
						</schmancy-surface>
					</div>
				`,
			)}
			${when(
				this.trailing,
				() => html`
					<div
						part="mark-trailing"
						aria-hidden="true"
						class="absolute top-1/2 right-0 z-10 grid size-11 translate-x-1/3 -translate-y-1/2 cursor-pointer place-items-center transition-transform duration-200 ease-out hover:scale-110"
						@click=${() => this.page(1)}
					>
						<schmancy-surface
							type="containerHighest"
							opacity="solid"
							class="grid size-full animate-[reach-right_2.8s_cubic-bezier(0.4,0,0.2,1)_infinite] place-items-center rounded-full ring-1 ring-control-border motion-reduce:animate-none"
						>
							<schmancy-icon>chevron_right</schmancy-icon>
						</schmancy-surface>
					</div>
				`,
			)}
		`
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'schmancy-scroll-rail': SchmancyScrollRail
	}
}
